import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';

import 'react-image-gallery/styles/css/image-gallery.css';
import ReactImageGallery, {
  ReactImageGalleryItem,
  ReactImageGalleryProps,
} from 'react-image-gallery';
import dynamic from 'next/dynamic';

import { ModalCloseButton } from '@components/Modal/next/Modal/utils';
import {
  ChevronLeft,
  ChevronRight,
  FullScreenIcon,
  VideoIcon,
} from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { useDevice } from '@contexts/device';
import { Entity } from '@components/views/hotel/detail/components/details-galleries-modal/types';
import { HotelDetailVideoViewModel } from '@api/v3';

import {
  ImageViewer,
  NavArrowsContainer,
  Arrow,
  HorizontalScrollableGalleriesList,
  GalleryListItemTitle,
  Title,
  CurrentSlideInfoDesktop,
  CurrentSlideInfoMobile,
  CloseImageViewerButton,
  GalleryListNavArrowContainer,
  GalleryHeaderWrapper,
  FullScreenButtonWrapper,
  VideoWrapper,
} from './GalleryStyles';
import { getDisplayName, getDisplayText } from './utils';
import { FullScreenGalleryControls } from './FullScreenGallery/FullScreenGallery';
import VideoGalleryItem from './GalleryVideoItem';
import { VideoThumbnail } from './GalleryVideoItem/DesktopGalleryVideoItem';

const FullScreenGallery = dynamic(
  () => import('./FullScreenGallery/FullScreenGallery')
);

type ImageGalleryProps = Omit<ReactImageGalleryProps, 'items'> & {
  galleries: { [key: string]: ReactImageGalleryProps['items'] };
  title: string;
  horizontalPadding?: `${number}%`;
  closeGallery?: () => void;
  closeModal?: () => void;
  activeGallery?: number;
  hideCategoryAll?: boolean;
  activeGalleryModalName?: string;
  startAtImage: number;
  initialGallery?: string;
  entityType?: Entity;
  videos?: HotelDetailVideoViewModel[];
  control?: boolean;
};

const FALLBACK_IMAGE_URL = '/static/images/setur-static.jpg';
const EMPTY_GALLERY_IMAGE_SET = [{ original: FALLBACK_IMAGE_URL }];

export const useGalleriesKeys = (
  galleries: ImageGalleryProps['galleries'],
  hideCategoryAll?: boolean
) => {
  const [keys] = useState(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { all, Videolar, Genel, Odalar, ...others } = galleries;

    const otherKeys = Object.keys(others);
    const General = Genel ? ['Genel'] : [];
    const Videos = Videolar ? ['Videolar'] : [];
    const RoomsTab = Odalar ? ['Odalar'] : [];

    const orderedKeys = hideCategoryAll
      ? [...Videos, ...General, ...otherKeys, ...RoomsTab]
      : ['all', ...Videos, ...General, ...otherKeys, ...RoomsTab];
    return orderedKeys;
  });

  return keys;
};

const ImageGallery = ({
  galleries,
  title,
  horizontalPadding = '9.24%' as `${number}%`,
  closeGallery,
  hideCategoryAll,
  activeGalleryModalName,
  startAtImage,
  initialGallery,
  closeModal,
  entityType,
  videos,
  control,
  ...rest
}: ImageGalleryProps) => {
  const { isDesktop } = useDevice();
  const keys = useGalleriesKeys(galleries, hideCategoryAll);
  const titleRef = useRef(null);
  const [activeGallery, setActiveGallery] = useState(() =>
    initialGallery ? keys.indexOf(initialGallery) : control ? 2 : 0
  );
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(startAtImage);
  const galleryRef = useRef<ReactImageGallery | null>(null);

  // =====================================
  // Disable body scroll when the gallery open
  // =====================================
  useEffect(() => {
    const body = document.body;
    if (body) {
      body.style.overflow = 'hidden';
    }

    return () => {
      if (body) {
        body.style.overflow = 'initial';
      }
    };
  }, []);

  // ==============================
  // Navigate the active slide index
  // ==============================
  const navigate = useCallback((index: number) => {
    galleryRef.current?.slideToIndex(
      galleryRef.current?.getCurrentIndex() + index
    );
  }, []);

  // ===========================
  // Toggle the active gallery
  // ===========================
  const navigateTitle = useCallback(
    (index: number) => {
      if (
        (activeGallery === 0 && index === -1) ||
        (keys.length - 1 === activeGallery && index === 1)
      ) {
        return;
      }

      document.querySelector(`#id${activeGallery}`)?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
        inline: 'center',
      });
      setCurrentSlideIndex(0);
      setActiveGallery(activeGallery + index);
    },
    [activeGallery]
  );

  useEffect(() => {
    if (!activeGalleryModalName) return;

    const targetGallery = galleries[activeGalleryModalName];

    if (targetGallery) {
      const targetIndex = keys.indexOf(activeGalleryModalName);

      if (targetIndex !== -1) {
        document.querySelector(`#id${targetIndex}`)?.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
          inline: 'center',
        });

        setActiveGallery((prevIndex) => {
          if (prevIndex !== targetIndex) {
            return targetIndex;
          }
          return prevIndex;
        });
      }
    }
  }, [activeGalleryModalName, galleries, keys]);

  // ======================
  // Go to the next slide
  // ======================
  const slideToNext = () => navigate(1);

  const items = useMemo(() => {
    const items: any[] = [];
    const videoItems: any[] = [];

    const activeGalleryItems = galleries[keys[activeGallery]];

    // Iterate over each item in activeGalleryItems
    activeGalleryItems?.forEach((item) => {
      // Assuming item has properties including 'original'
      items.push({
        ...item, // Make sure item conforms to ReactImageGalleryItem
      });
    });
    items.forEach((i) =>
      i?.hotelImageCategoryName === 'Videolar'
        ? videos?.map((video) => {
            videoItems.push({
              ...video,
              embedUrl: video.embedUrl,
              thumbnail: video.description || 'Setur',
              renderItem: (item) => (
                <VideoGalleryItem
                  video={video}
                  item={item}
                  isFullScreen={isFullScreen}
                />
              ),
              renderThumbInner: (item) => (
                <VideoThumbnail item={item} video={video} />
              ),
            });
          })
        : null
    );
    // Return a concatenated array of videoItems and items
    if (
      (
        activeGalleryItems?.[0] as ReactImageGalleryItem & {
          hotelImageCategoryName: string;
        }
      )?.hotelImageCategoryName === 'Videolar'
    ) {
      return [...videoItems];
    } else return [...items];
  }, [galleries, keys, activeGallery, videos, isFullScreen]);

  // ======================
  // Go to the previous slide
  // ======================
  const slideToPrevious = () => navigate(-1);

  return (
    <ImageViewer
      isVideoTab={keys.some((item) => item === 'Videolar')}
      horizontalPadding={horizontalPadding}
      isFullScreen={isFullScreen}
    >
      <GalleryHeaderWrapper>
        <Title>{title}</Title>
        {isDesktop ? (
          <FullScreenButtonWrapper>
            <CustomIcon
              icon={FullScreenIcon}
              paletteColor='white'
              fontSize={20}
              clickable
              onClick={() => galleryRef?.current?.fullScreen()}
            />
            {entityType === 'hotel' ? (
              <ModalCloseButton paletteColor='NL0' onClick={closeModal} />
            ) : null}
          </FullScreenButtonWrapper>
        ) : null}
      </GalleryHeaderWrapper>
      {items?.length ? (
        <CurrentSlideInfoMobile>
          <span>{getDisplayName(keys[activeGallery])}</span>
          &nbsp;
          <span>
            {currentSlideIndex + 1}/
            {videos && keys.some((item) => item === 'Videolar')
              ? videos?.length
              : items?.length}
          </span>
        </CurrentSlideInfoMobile>
      ) : null}
      <CloseImageViewerButton
        onClick={closeGallery}
        noLabel
        paletteColor='white'
      />

      <GalleryListNavArrowContainer>
        <Arrow direction='prev' onClick={() => navigateTitle(-1)}>
          <ChevronLeft />
        </Arrow>
        <Arrow direction='next' onClick={() => navigateTitle(1)}>
          <ChevronRight />
        </Arrow>
      </GalleryListNavArrowContainer>
      <HorizontalScrollableGalleriesList activeItem={`${activeGallery}`}>
        {keys?.map((categoryName, galleryIndex) => (
          <GalleryListItemTitle
            id={`id${galleryIndex}`}
            ref={titleRef}
            key={categoryName}
            data-item={galleryIndex.toString()}
            onClick={(e) => {
              e.currentTarget?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'center',
              });

              setActiveGallery(galleryIndex);
              setCurrentSlideIndex(0);
            }}
          >
            {categoryName === 'Videolar' ? (
              <>
                <VideoWrapper>
                  <CustomIcon
                    icon={VideoIcon}
                    paletteColor='D400'
                    fontSize={15}
                  />
                </VideoWrapper>
                {getDisplayText(categoryName, videos?.length ?? 0)}
              </>
            ) : (
              <>
                {getDisplayText(
                  categoryName,
                  galleries[categoryName]?.length ?? 0
                )}
              </>
            )}
          </GalleryListItemTitle>
        ))}
      </HorizontalScrollableGalleriesList>

      {items?.length ? (
        <CurrentSlideInfoDesktop>
          <span>{getDisplayName(keys[activeGallery])}</span>

          <span>
            {currentSlideIndex + 1}/
            {items.some((item) => item.hotelImageCategoryName === 'Videolar')
              ? videos?.length
              : items?.length}
          </span>
        </CurrentSlideInfoDesktop>
      ) : null}
      <ReactImageGallery
        ref={(gallery) => (galleryRef.current = gallery)}
        showPlayButton={false}
        showFullscreenButton={false}
        startIndex={currentSlideIndex}
        {...rest}
        showNav={false}
        items={items?.length ? items : EMPTY_GALLERY_IMAGE_SET}
        onSlide={setCurrentSlideIndex}
        onErrorImageURL={FALLBACK_IMAGE_URL}
        onScreenChange={(isFullScreen) => setIsFullScreen(isFullScreen)}
        showThumbnails={Boolean(!isFullScreen)}
        renderItem={
          isFullScreen
            ? (item) => (
                <FullScreenGallery
                  item={item}
                  title={title}
                  hasItems={Boolean(items?.length)}
                  totalItemsInfo={`${currentSlideIndex + 1}/ ${
                    videos && keys.some((item) => item === 'Videolar')
                      ? videos?.length
                      : items?.length
                  }`}
                  displayName={getDisplayName(keys[activeGallery])}
                />
              )
            : undefined
        }
        renderCustomControls={() =>
          isFullScreen && isDesktop ? (
            <FullScreenGalleryControls
              nextSlide={slideToNext}
              previousSlide={slideToPrevious}
              existFullScreen={() => galleryRef.current.exitFullScreen()}
            />
          ) : undefined
        }
      />
      {items?.length > 1 ? (
        <NavArrowsContainer>
          <Arrow direction='prev' onClick={slideToPrevious}>
            <ChevronLeft />
          </Arrow>
          <Arrow direction='next' onClick={slideToNext}>
            <ChevronRight />
          </Arrow>
        </NavArrowsContainer>
      ) : null}
    </ImageViewer>
  );
};

export default ImageGallery;
